import Tooltip from 'rc-tooltip';
import {useEffect, useRef, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import 'rc-tooltip/assets/bootstrap.css';
import {createPortal} from 'react-dom';
import {pluralize} from '~/common/utils';
import {WhoIsComingOverlay} from '~/ViewInvitation/common/WhoIsComingOverlay';
import {isPreview} from '~/ViewInvitation/utils';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {InfoIcon} from '~library/svg/InfoIcon';
import {SettingsLineIcon} from '~library/svg/SettingsLineIcon';
import {CrownIcon} from '~library/svg/v2/CrownIcon';
import {largishAndUp, link} from '~libSass/base/_exports.sass';

export const WhosComingModule = ({readonly, connectCTA}) => {
  const location = useLocation();
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const ready = useRef(false);

  const {rsvpDetails} = useSelector(
    ({guestList: s}) => ({rsvpDetails: s.rsvpDetails}),
    shallowEqual
  );
  const guestPreviews = useSelector(
    (state) => state?.whoIsComing?.guestPreviews ?? [],
    shallowEqual
  );

  const hostUserId = useSelector((state) => state.eventDetails.hostUserId);
  const origin = useSelector((state) => state.eventDetails.origin);
  const allowKids = useSelector((state) => !!state.eventDetails.allowKids);

  const isPrivateGuestlist = useSelector(
    (state) => state?.whoIsComing?.isPrivateGuestlist ?? false
  );

  const isHost = useSelector((state) => state.user?.isHost ?? false, shallowEqual);

  const [overlayOpen, setOverlayOpen] = useState(false);

  const sortGuests = (a, b) => {
    if (a.userId === hostUserId) return -1;
    if (b.userId === hostUserId) return 1;
    return 0;
  };

  const preview = isPreview();

  useEffect(() => {
    if (ready.current) {
      const params = new URLSearchParams(location.search);
      const utmSource = params.get('utm_source');
      if (utmSource === 'GUEST_FEED_REPLY') {
        setOverlayOpen(true);
      } else {
        const urlPieces = location.pathname.split('/');
        const page = urlPieces.length > 0 ? urlPieces[urlPieces.length - 1] : '';
        if (page === 'guestlist') {
          // Open guestlist overlay if the user is at the /guestlist URL
          setOverlayOpen(true);
        }
      }
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    ready.current = true;
  }, []);

  return (
    <>
      <button
        className={`unstyled-button view-invitation__preview-cta ${
          !preview || readonly ? 'hidden' : ''
        }`}
        data-qa-id="guest-options"
        id="guest-options"
        ref={(elem) => connectCTA && connectCTA('guest-options', elem)}
        type="button"
      >
        <SettingsLineIcon ratio={0.5} color={link} />
        Guest Options
      </button>
      <div className="view-invitation__who-is-coming__module-title-wrapper">
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Typography
            variant="header3"
            data-qa-id="who-is-coming-module-title"
            classNames="who-is-coming__module-title"
          >
            Who&apos;s coming
          </Typography>
          {origin === 21 && preview && (
            <Tooltip
              placement="top"
              trigger={['hover', 'click']}
              overlay={
                <span>
                  <strong>Example Guest List:</strong> This will become a real list of guests who
                  RSVP &quot;Yes&quot; after you send your invitation.
                </span>
              }
              overlayClassName="who-is-coming__rc-tooltip"
            >
              <span className="who-is-coming__info-popover" data-qa-id="who-is-coming-info">
                <InfoIcon className="event-details__info" />
              </span>
            </Tooltip>
          )}
        </div>
        {(!isPrivateGuestlist || isHost) && (
          <button
            className="unstyled-button link large"
            data-qa-id="see-all-guests"
            onClick={() => setOverlayOpen(true)}
            disabled={preview}
            type="button"
          >
            See all guests
          </button>
        )}
      </div>
      {isPrivateGuestlist && !isHost
        ? <Typography variant="paragraph2" data-qa-id="who-is-coming-hidden-list">
            Your host has hidden the guest list for this event.
          </Typography>
        : <>
            <div
              className="view-invitation__who-is-coming__guest-preview-grid"
              data-qa-id="who-is-coming-guest-list"
            >
              {guestPreviews
                .sort(sortGuests)
                .slice(0, 5)
                .map((guest) => (
                  <div
                    className="view-invitation__who-is-coming__guest"
                    style={{
                      gridRow: guest.rsvpComment && largeScreens ? 'span 2' : undefined,
                    }}
                    key={`guest_${guest.userId}`}
                  >
                    <div className="view-invitation__who-is-coming__guest-details">
                      <p
                        className="view-invitation__who-is-coming__guest-name"
                        data-qa-id="guest-name"
                        data-qa-value={guest.guestName.toLowerCase().replaceAll(' ', '-')}
                      >
                        {guest.guestName}
                        {guest.userId === hostUserId ? ' (Host)' : ''}
                        {guest.guestOfHonor && <CrownIcon ratio="0.5" />}
                      </p>
                      <Typography
                        variant="paragraph3"
                        color="emeraldWebText"
                        data-qa-id="guest-count"
                        classNames="view-invitation__who-is-coming__guest-rsvp-count"
                      >
                        {pluralize(allowKids ? 'adult' : 'guest', guest.adults)}
                        {allowKids &&
                          guest.kids > 0 &&
                          `| ${guest.kids} ${guest.kids > 1 ? 'kids' : 'kid'}`}
                      </Typography>
                    </div>
                    {guest.rsvpComment && (
                      <div className="view-invitation__who-is-coming__rsvp-message">
                        <Typography
                          variant="paragraph2"
                          color="charcoal"
                          data-qa-id="guest-rsvp-comment"
                        >
                          {guest.rsvpComment.slice(0, 150)}
                          {guest.rsvpComment.length > 150 ? '...' : ''}
                        </Typography>
                      </div>
                    )}
                  </div>
                ))}
            </div>
            {!preview &&
              createPortal(
                <WhoIsComingOverlay
                  open={overlayOpen}
                  onClose={() => setOverlayOpen(false)}
                  rsvpDetails={rsvpDetails}
                  preview={preview}
                />,
                document.body
              )}
          </>}
    </>
  );
};
